
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingRouteActionDelay struct
 */
class TradingRouteActionDelay {
  /**
   * Initialize struct
   * @param {!number=} delayMin
   * @param {!number=} delayMax
   * @constructor
   */
  constructor (delayMin = new UInt64(0, 0), delayMax = new UInt64(0, 0)) {
    this.DelayMin = delayMin
    this.DelayMax = delayMax
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingRouteActionDelay}
   * @param {!TradingRouteActionDelay} other Other struct
   * @returns {!TradingRouteActionDelay} This struct
   */
  copy (other) {
    if (other.DelayMin != null) {
      this.DelayMin = UInt64.fromNumber(other.DelayMin)
    } else {
      this.DelayMin = null
    }
    if (other.DelayMax != null) {
      this.DelayMax = UInt64.fromNumber(other.DelayMax)
    } else {
      this.DelayMax = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingRouteActionDelay}
   * @returns {!TradingRouteActionDelay} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingRouteActionDelayModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingRouteActionDelayModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingRouteActionDelay}
   * @param {!TradingRouteActionDelay} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingRouteActionDelay)) {
      throw new TypeError('Instance of TradingRouteActionDelay is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingRouteActionDelay}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DelayMin: ((this.DelayMin != null) ? this.DelayMin.toNumber() : null),
      DelayMax: ((this.DelayMax != null) ? this.DelayMax.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingRouteActionDelay.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingRouteActionDelay} other Object value
   * @returns {!TradingRouteActionDelay} Created struct
   */
  static fromObject (other) {
    return new TradingRouteActionDelay().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingRouteActionDelay}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingRouteActionDelay.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingRouteActionDelay}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 65
  }
}

export { TradingRouteActionDelay };

/**
 * Fast Binary Encoding TradingRouteActionDelay field model
 */
class FieldModelTradingRouteActionDelay extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DelayMin = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.DelayMax = new fbe.FieldModelUInt64(buffer, this.DelayMin.fbeOffset + this.DelayMin.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DelayMin.fbeSize + this.DelayMax.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DelayMin.fbeExtra + this.DelayMax.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingRouteActionDelay.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 65
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DelayMin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DelayMin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DelayMin.fbeSize

    if ((fbeCurrentSize + this.DelayMax.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DelayMax.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DelayMax.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!TradingRouteActionDelay} fbeValue Default value, defaults is new TradingRouteActionDelay()
   * @returns {!TradingRouteActionDelay} TradingRouteActionDelay value
   */
  get (fbeValue = new TradingRouteActionDelay()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!TradingRouteActionDelay} fbeValue TradingRouteActionDelay value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DelayMin.fbeSize) <= fbeStructSize) {
      fbeValue.DelayMin = this.DelayMin.get()
    } else {
      fbeValue.DelayMin = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DelayMin.fbeSize

    if ((fbeCurrentSize + this.DelayMax.fbeSize) <= fbeStructSize) {
      fbeValue.DelayMax = this.DelayMax.get()
    } else {
      fbeValue.DelayMax = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DelayMax.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingRouteActionDelay}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {!TradingRouteActionDelay} fbeValue TradingRouteActionDelay value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingRouteActionDelay}
   * @param {TradingRouteActionDelay} fbeValue TradingRouteActionDelay value
   */
  setFields (fbeValue) {
    this.DelayMin.set(fbeValue.DelayMin)
    this.DelayMax.set(fbeValue.DelayMax)
  }
}

export { FieldModelTradingRouteActionDelay };

/**
 * Fast Binary Encoding TradingRouteActionDelay model
 */
class TradingRouteActionDelayModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingRouteActionDelay(this.buffer, 4)
  }

  /**
   * Get the TradingRouteActionDelay model
   * @this {!TradingRouteActionDelayModel}
   * @returns {!FieldModelTradingRouteActionDelay} model TradingRouteActionDelay model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingRouteActionDelayModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingRouteActionDelayModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingRouteActionDelayModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingRouteActionDelayModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingRouteActionDelay.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingRouteActionDelayModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingRouteActionDelayModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingRouteActionDelayModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingRouteActionDelayModel}
   * @param {!TradingRouteActionDelay} value TradingRouteActionDelay value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingRouteActionDelayModel}
   * @param {!TradingRouteActionDelay} value TradingRouteActionDelay value, defaults is new TradingRouteActionDelay()
   * @return {!object} Deserialized TradingRouteActionDelay value and its size
   */
  deserialize (value = new TradingRouteActionDelay()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingRouteActionDelay(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingRouteActionDelay(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingRouteActionDelayModel}
   * @param {!number} prev Previous TradingRouteActionDelay model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingRouteActionDelayModel };
